import React, { useState, useEffect } from 'react';
import { getUsers, updateUser, deleteUser, adminResetPassword, adminRegisterWalkin } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Users, MoreVertical, Shield, Trash2, Filter, KeyRound, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

export default function ManageUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [editUser, setEditUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [showWalkin, setShowWalkin] = useState(false);
  const [walkinName, setWalkinName] = useState('');
  const [walkinPin, setWalkinPin] = useState('');
  const [walkinPhone, setWalkinPhone] = useState('');
  const [walkinEmail, setWalkinEmail] = useState('');
  const [walkinResult, setWalkinResult] = useState(null);

  useEffect(() => {
    loadUsers();
  }, [roleFilter]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (roleFilter !== 'all') params.role = roleFilter;
      const { data } = await getUsers(params);
      setUsers(data.users || []);
    } catch (e) {
      console.error('Failed to load users', e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editUser || !newRole) return;
    try {
      await updateUser(editUser.user_id, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      setEditUser(null);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUser(deleteTarget.user_id);
      toast.success('User deleted');
      setDeleteTarget(null);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    if (!resetTarget || !resetPassword) return;
    try {
      await adminResetPassword(resetTarget.user_id, resetPassword);
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null);
      setResetPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleWalkinRegister = async () => {
    if (!walkinName.trim() || !walkinPin) { toast.error('Name and PIN are required'); return; }
    try {
      const { data } = await adminRegisterWalkin({
        name: walkinName.trim(), pin: walkinPin, email: walkinEmail, phone: walkinPhone
      });
      setWalkinResult(data);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to register');
    }
  };

  const roleBadge = (role) => {
    const styles = {
      admin: 'bg-[#D3735A]/10 text-[#D3735A] border-[#D3735A]/20',
      supervisor: 'bg-[#4A788C]/10 text-[#4A788C] border-[#4A788C]/20',
      volunteer: 'bg-[#3F6352]/10 text-[#3F6352] border-[#3F6352]/20',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold border ${styles[role] || styles.volunteer}`}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  return (
    <div className="animate-fade-in" data-testid="manage-users-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] tracking-tight">Manage Users</h1>
          <p className="text-[#5C5855] mt-1">View and manage all users in the system</p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => { setShowWalkin(true); setWalkinResult(null); setWalkinName(''); setWalkinPin(''); setWalkinPhone(''); setWalkinEmail(''); }}
            className="bg-[#3F6352] hover:bg-[#2C4A3C] text-white rounded-lg px-4 py-2 font-semibold text-sm"
            data-testid="walkin-register-btn">
            <UserPlus className="h-4 w-4 mr-2" /> Walk-in Register
          </Button>
          <Filter className="h-4 w-4 text-[#5C5855]" />
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="role-filter" className="w-[160px] border-[#D1C9C0] bg-white">
              <SelectValue placeholder="Filter role" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="volunteer">Volunteers</SelectItem>
              <SelectItem value="supervisor">Supervisors</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="w-full rounded-xl border border-[#E8E3DD] overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FAF8F5] border-b border-[#E8E3DD]">
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Name</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Email</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Role</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Volunteer ID</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i} className="border-b border-[#E8E3DD]">
                  <TableCell colSpan={5} className="px-6 py-4"><div className="h-4 bg-[#F2EFEB] rounded animate-pulse" /></TableCell>
                </TableRow>
              ))
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-[#8A847F]">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No users found</p>
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.user_id} className="border-b border-[#E8E3DD] hover:bg-[#F2EFEB]/50 transition-colors" data-testid={`user-row-${u.user_id}`}>
                  <TableCell className="px-6 py-4 text-sm font-semibold text-[#2D2B2A]">{u.name}</TableCell>
                  <TableCell className="px-6 py-4 text-sm text-[#5C5855]">{u.email}</TableCell>
                  <TableCell className="px-6 py-4">{roleBadge(u.role)}</TableCell>
                  <TableCell className="px-6 py-4 text-sm text-[#8A847F] font-mono">{u.volunteer_id || '—'}</TableCell>
                  <TableCell className="px-6 py-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`user-actions-${u.user_id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-white" align="end">
                        <DropdownMenuItem onClick={() => { setEditUser(u); setNewRole(u.role); }} data-testid={`change-role-${u.user_id}`}>
                          <Shield className="h-4 w-4 mr-2" /> Change Role
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setResetTarget(u); setResetPassword(''); }} data-testid={`reset-password-${u.user_id}`}>
                          <KeyRound className="h-4 w-4 mr-2" /> Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteTarget(u)} className="text-[#B74134]" data-testid={`delete-user-${u.user_id}`}>
                          <Trash2 className="h-4 w-4 mr-2" /> Delete User
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Role Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent className="bg-white" data-testid="edit-role-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Change Role for {editUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger className="border-[#D1C9C0] bg-white" data-testid="new-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="volunteer">Volunteer</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="border-[#D1C9C0]">Cancel</Button>
            <Button onClick={handleUpdateRole} className="bg-[#D3735A] hover:bg-[#BD6047] text-white" data-testid="save-role-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="bg-white" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Delete User</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C5855]">Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="border-[#D1C9C0]">Cancel</Button>
            <Button onClick={handleDelete} className="bg-[#B74134] hover:bg-[#9a3229] text-white" data-testid="confirm-delete-button">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Reset Password Dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(open) => !open && setResetTarget(null)}>
        <DialogContent className="bg-white" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Reset Password for {resetTarget?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="block text-sm font-semibold text-[#5C5855] mb-2">New Password</Label>
            <Input
              data-testid="admin-new-password-input"
              type="password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              placeholder="Min 6 characters"
              className="border-[#D1C9C0]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)} className="border-[#D1C9C0]">Cancel</Button>
            <Button onClick={handleResetPassword} disabled={resetPassword.length < 6} className="bg-[#D3735A] hover:bg-[#BD6047] text-white" data-testid="admin-reset-password-btn">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Walk-in Registration Dialog */}
      <Dialog open={showWalkin} onOpenChange={setShowWalkin}>
        <DialogContent className="bg-white" data-testid="walkin-register-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Walk-in Volunteer Registration</DialogTitle>
          </DialogHeader>
          {walkinResult ? (
            <div className="space-y-4 text-center py-4" data-testid="walkin-success">
              <div className="bg-[#2C5545]/10 rounded-xl p-6">
                <p className="text-sm text-[#5C5855] mb-2">Volunteer registered successfully!</p>
                <p className="text-2xl font-bold text-[#2D2B2A] font-mono" data-testid="walkin-vol-id">{walkinResult.volunteer_id}</p>
                <p className="text-lg font-semibold text-[#2D2B2A] mt-2">{walkinResult.name}</p>
              </div>
              <p className="text-sm text-[#5C5855]">Give the volunteer their ID card. They can log in with their Volunteer ID and PIN.</p>
              <Button onClick={() => setShowWalkin(false)} className="bg-[#D3735A] hover:bg-[#BD6047] text-white">Done</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Name *</Label>
                <Input value={walkinName} onChange={(e) => setWalkinName(e.target.value)} placeholder="Volunteer's full name" className="border-[#D1C9C0]" data-testid="walkin-name" />
              </div>
              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">PIN (4-6 digits) *</Label>
                <Input type="text" value={walkinPin} onChange={(e) => setWalkinPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="e.g. 1234" inputMode="numeric" className="border-[#D1C9C0] font-mono tracking-wider" data-testid="walkin-pin" />
              </div>
              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Phone (optional)</Label>
                <Input value={walkinPhone} onChange={(e) => setWalkinPhone(e.target.value)} placeholder="(555) 123-4567" className="border-[#D1C9C0]" data-testid="walkin-phone" />
              </div>
              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Email (optional)</Label>
                <Input type="email" value={walkinEmail} onChange={(e) => setWalkinEmail(e.target.value)} placeholder="Optional email" className="border-[#D1C9C0]" data-testid="walkin-email" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowWalkin(false)} className="border-[#D1C9C0]">Cancel</Button>
                <Button onClick={handleWalkinRegister} className="bg-[#3F6352] hover:bg-[#2C4A3C] text-white" data-testid="walkin-submit">Register Volunteer</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
